import * as _ from 'underscore';
import { util } from '../../shared/util';
import { IUserData, IUserWebshop } from '../../shared/services/auth/auth-data.service';
import { ICodeDescription, ICodeValue, ICompany, ICompanyCountry, ICountry, IDescribedObject, IGenericUser, ILanguage, ILocationExtended, INamedStringObject, ISubCompany, IValueObject } from '../../../type_definitions/datatypes';
import { IPermissionRequest, IUserNotifications, IUserProfileRequest, UserProfileDataService } from './userprofile-data.service';
import { AuthService } from '../../shared/services/auth/auth.service';
import { IOrderQuotePermissionResponse, IWebshopPermission, IWebshopPermissionResponse, UserDataService } from '../../shared/user/user-data.service';
import { CompanyType } from '../../shared/company/company-data.service';
import { GaFormlyService } from '../../shared/formly/ga-formly.service';
import { Injectable } from '@angular/core';
import { IDualMultiSelectElement } from '../../shared/dual-multi-select/dual-multi-select.component';

export interface IUserInfo {
  userId:number;
  firstName:string;
  lastName:string;
  email:string;
  username:string;
  userType:string;
  jobTitle:string;
  companyDepartment?:ICompanyCountry;
  directPhone:string;
  officePhone:string;
  cellPhone:string;
  fax:string;
  officeCountry:ICountry;
  officeLocation:ILocationExtended;
  officeAddress:string;
  office:string;
  language:ILanguage;
  gaLayout:IDescribedObject;
  registrationState:string;
  password?:string;
  company:ICompany;
  signupCompany:string;
  signupSubCompany:string;
  signupCompanyType?:string;
  signupRole?:string;
  subCompany?:ISubCompany;
  companyType?:INamedStringObject;
  privacyAgreed:boolean;
  preferredCommunication?:string;
  comment:string;
  genericUser:IGenericUser;
  userGroup:ICodeDescription;
  localCredentialsAlwaysAllowed:boolean;
  lastTrainingDate?:number;
  trainingStatus?:IValueObject;
  trainingComments?:string;
  weChatId?:string;
  whatsAppId?:string;
  mailSubscription?:IUserNotifications;
  responsive?:boolean;
  usingGA?:boolean;
  canDemoGA?:boolean;
  linkedin?:string;
  fluentEnglish?:IValueObject;
  updateMethod?:ICodeValue;
  updateComments?:string;
  updateFrequency?:string;
  timeSlotForGAUpdates?:string;
  checkpoint?:string;
  userWebshops?:IUserWebshop[];
  timezone?:string;
}

export interface ITrainingData {
  lastTrainingDate:number;
  trainingStatus:IValueObject;
  trainingComments:string;
  trainingModification?:string;
  responsive?:boolean;
  usingGA?:boolean;
  canDemoGA?:boolean;
}

interface IServerData {
  userProfile:IUserData;
  permissions:IWebshopPermissionResponse | IOrderQuotePermissionResponse;
}

enum userTypes {
  // noinspection JSUnusedGlobalSymbols
  ACSL,
  PARTNER,
  LAMPARTNER,
  VENDOR,
  CUSTOMER,
  DISTRIBUTOR,
  BANK,
  SUPPLIER,
  UNKNOWN
}

const webshopPermissionsUserTypes = [
  userTypes[ userTypes.PARTNER ],
  userTypes[ userTypes.LAMPARTNER ],
  userTypes[ userTypes.VENDOR ]
];

const PARTNER_LAM_USER:string = 'PARTNER_LAM_USER';
export const USERPROFILE_WIZARD:string = 'userprofile';

@Injectable()
export class UserProfileService {
  public static TYPE_PO:string = 'PO';
  public static TYPE_PQ:string = 'PQ';
  public static NEW_USER:string = 'NEW';

  private userProfilePromise:Promise<IUserData>;
  private genericUsersPromise:Promise<IGenericUser[]>;
  private userProfileInfoData:IUserInfo;
  private genericUsers:IGenericUser[];
  private userProfileId:number;
  private permissions:IWebshopPermissionResponse | IOrderQuotePermissionResponse;
  private webshopPermissions:IWebshopPermission;
  private notifications:IUserNotifications;
  private backupServerData:IServerData = {
    userProfile: undefined,
    permissions: undefined
  };
  private backupUserProfileInfoData:IUserInfo;
  private webshopId:number;
  private companyId:number;
  private countryId:number;
  private _genericUserFn:Function;

  constructor(
    private authService:AuthService,
    private formlyService:GaFormlyService,
    private userProfileDataService:UserProfileDataService,
    private userDataService:UserDataService
  ) {/**/
  }


  // set the promise for the selected user (current user / userId specified in url parameter)
  public setUserProfileId(userId?:string):void {
    if (userId === UserProfileService.NEW_USER) {
      const newUserData:IUserData = <any>{
        language: {
          id: 'eng'
        }
      };
      this.setUserData(newUserData);
      this.userProfilePromise = Promise.resolve(newUserData);
    } else if (userId) {
      // load the userdata
      // set it
      // load the list of generic users
      let userData:IUserData;

      this.userProfileId = Number(userId);
      this.userProfilePromise = this.userProfileDataService.getUserProfile(this.userProfileId)
        .then((ud:IUserData) => {
          userData = ud;
          this.setUserData(ud);
        })
        .then(this.loadGenericUsers)
        .then(() => userData);
    } else {
      const currentUser = this.authService.getCurrentUser();
      this.userProfileId = currentUser.id;
      this.setUserData(currentUser);
      this.userProfilePromise = this.loadGenericUsers()
        .then(() => currentUser);
    }
  }

  public getUserProfilePromise():Promise<IUserData> {
    return this.userProfilePromise;
  }

  public getGenericUsersPromise():Promise<IGenericUser[]> {
    return this.genericUsersPromise;
  }

  public getUserProfileId():number {
    return this.userProfileId;
  }

  public updateUserProfileId(id:number):void {
    this.userProfileId = id;
  }

  public setUserData(userProfile:IUserData):void {
    this.userProfileId = userProfile.id;
    this.userProfilePromise = Promise.resolve(userProfile);
    this.userProfileInfoData = {
      userId: userProfile.id,
      firstName: userProfile.firstname,
      lastName: userProfile.lastname,
      email: userProfile.email,
      username: userProfile.login,
      userType: userProfile.userType,
      jobTitle: userProfile.title,
      directPhone: userProfile.directPhone,
      officePhone: userProfile.officePhone,
      cellPhone: userProfile.cellPhone,
      fax: userProfile.faxNumber,
      officeCountry: userProfile.location?.companyCountry?.country || userProfile.signupCountry,
      officeLocation: userProfile.location,
      officeAddress: undefined,
      office: userProfile.office,
      language: userProfile.language,
      gaLayout: userProfile.gaLayout,
      registrationState: userProfile.registrationState,
      company: userProfile.location?.companyCountry?.company,
      companyDepartment: userProfile.companyDepartment,
      signupCompany: userProfile.signupCompany,
      signupSubCompany: userProfile.signupSubCompany,
      signupCompanyType: userProfile.signupCompanyType,
      signupRole: userProfile.signupRole,
      subCompany: userProfile.subCompany,
      preferredCommunication: userProfile.preferredCommunication,
      privacyAgreed: userProfile.privacyAgreed,
      comment: userProfile.comment,
      genericUser: userProfile.genericUser,
      userGroup: userProfile.userGroup,
      localCredentialsAlwaysAllowed: userProfile.localCredentialsAlwaysAllowed,
      lastTrainingDate: userProfile.userTraining?.lastTrainingDate,
      trainingStatus: userProfile.userTraining?.trainingStatus? { id: userProfile.userTraining.trainingStatus } : undefined,
      trainingComments: userProfile.userTraining?.acslComment,
      weChatId: userProfile.weChatId,
      whatsAppId: userProfile.whatsappId,
      responsive: userProfile.userTraining?.responsive,
      usingGA: userProfile.userTraining?.usingGA,
      canDemoGA: userProfile.userTraining?.canDemoGA,
      linkedin: userProfile.linkedin,
      fluentEnglish: util.notNullOrUndefined(userProfile?.fluentEnglish)? <IValueObject>this.formlyService.getBooleanOptions(userProfile?.fluentEnglish) : undefined,
      updateMethod: userProfile.updateMethod,
      updateComments: userProfile.updateComments,
      updateFrequency: userProfile.updateFrequency,
      timeSlotForGAUpdates: userProfile.timeSlotForGAUpdates,
      checkpoint: userProfile.checkpoint,
      userWebshops: userProfile.userWebshops,
      timezone: userProfile.timezone
    };

    // when the country is dummy, we know that it isn't a proper location
    if (this.userProfileInfoData.officeCountry?.id === 999) {
      this.userProfileInfoData.officeCountry = userProfile.signupCountry?.id !== 999? userProfile.signupCountry : undefined;
      this.userProfileInfoData.officeLocation = undefined;
    }

    // backup user profile
    this.backupServerData.userProfile = userProfile;
    this.backupUserProfileInfoData = _.clone(this.userProfileInfoData);
  }

  public loadGenericUsers = ():Promise<IGenericUser[]> => {
    const companyId:number = this.userProfileInfoData && this.userProfileInfoData.company && this.userProfileInfoData.company.id;
    if (!companyId) {
      // when no company has been selected, we cannot know the generic users
      console.log('No company selected');
      return Promise.resolve([]);
    }

    if (!this.manageExternalUser()) {
      // looking at own profile => no need to load the list
      return Promise.resolve([]);
    }

    return this.genericUsersPromise = this.userDataService.getGenericUsers(companyId, this.webshopId)
      .then((genericUsers:IGenericUser[]) => {
        if (!this.userProfileInfoData?.genericUser) {
          // a custom generic user selection function has priority over the default selection
          if (this._genericUserFn) {
            this.userProfileInfoData.genericUser = this._genericUserFn(genericUsers);
          }

          //preselect a generic user
          if (!this.userProfileInfoData.genericUser) {
            const defaultUsers:IGenericUser[] = _.filter(genericUsers, (gu:IGenericUser) => gu.defaultGenericUser);
            if (defaultUsers.length) {
              this.userProfileInfoData.genericUser = _.find(defaultUsers, (gu:IGenericUser) => gu.ofMainWebshop) || defaultUsers[ 0 ];
            }
          }
        }

        this.genericUsers = genericUsers;
        return genericUsers;
      });
  };

  public getGenericUsers():IGenericUser[] {
    return this.genericUsers;
  }

  public updateGenericUser(genericUser:IGenericUser):void {
    this.userProfileInfoData.genericUser = genericUser;
  }

  public updatePermissions(permissions:{ webshopPermissions:IWebshopPermission }):void {
    if (permissions.webshopPermissions) {
      this.permissions = <IWebshopPermissionResponse>{
        userType: permissions.webshopPermissions.userType,
        permissionToAllWebshops: permissions.webshopPermissions.permissionToAllWebshops && permissions.webshopPermissions.permissionToAllWebshops.id,
        selectedWebshops: permissions.webshopPermissions.selectedWebshops,
        possibleWebshops: permissions.webshopPermissions.possibleWebshops
      };

      this.webshopPermissions = permissions.webshopPermissions;
    }
  }

  public updateUserTrainingData(trainingData:ITrainingData):void {
    this.userProfileInfoData = this.userProfileInfoData || <any>{};
    this.userProfileInfoData.lastTrainingDate = trainingData.lastTrainingDate;
    this.userProfileInfoData.trainingStatus = trainingData.trainingStatus;
    this.userProfileInfoData.trainingComments = trainingData.trainingComments;
    this.userProfileInfoData.responsive = trainingData.responsive;
    this.userProfileInfoData.usingGA = trainingData.usingGA;
    this.userProfileInfoData.canDemoGA = trainingData.canDemoGA;
  }

  public getWebshopPermissions():IWebshopPermission {
    return this.webshopPermissions;
  }

  public loadPermissions(forceReload?:boolean):Promise<IWebshopPermissionResponse | IOrderQuotePermissionResponse> {
    if (!this.permissions || forceReload) {
      return this.userDataService.getPermissions(this.getUserProfileId())
        .then((permissions:IWebshopPermissionResponse | IOrderQuotePermissionResponse) => {
          this.permissions = permissions;
          this.backupServerData.permissions = util.copyAndExtend(permissions);

          if (permissions.userType && this.userProfileInfoData) {
            this.userProfileInfoData.userType = permissions.userType;
          }
          return permissions;
        });
    } else {
      return Promise.resolve(this.permissions);
    }
  }

  public getUserInfo():IUserInfo {
    return this.userProfileInfoData;
  }

  public getUserInfoBackup():IUserInfo {
    return this.backupUserProfileInfoData;
  }

  public setCompanyId(companyId:string):void {
    this.companyId = companyId && parseInt(companyId);
  }

  public setCountryId(countryId:string):void {
    this.countryId = countryId && parseInt(countryId);
  }

  public setFirstName(firstName:string):void {
    this.userProfileInfoData.firstName = firstName;
  }

  public setWebshopId(webshopId:string):void {
    this.webshopId = webshopId && parseInt(webshopId);
  }

  public getCompanyId():number {
    return this.companyId;
  }

  public getCountryId():number {
    return this.countryId;
  }

  public getWebshopId():number {
    return this.webshopId;
  }

  // determine if the authenticated user manages another profile
  public manageExternalUser():boolean {
    return (this.authService.getCurrentUser().id !== this.getUserProfileId());
  }

  // used to display the ACSL permissions view
  public isACSLUser():boolean {
    return this.userProfileInfoData?.userType === userTypes[ userTypes.ACSL ];
  }

  // used to display the customer permissions view and access settings view
  public isCustomerUser():boolean {
    return this.userProfileInfoData?.userType === userTypes[ userTypes.CUSTOMER ];
  }

  public isVendorUser():boolean {
    return this.userProfileInfoData?.userType === userTypes[ userTypes.VENDOR ];
  }

  public isPartnerLAMUser():boolean {
    return this.userProfileInfoData?.company?.companyType === CompanyType.BP && this.userProfileInfoData?.userGroup?.code === PARTNER_LAM_USER;
  }

  public isPartnerNAMUser():boolean {
    return this.userProfileInfoData?.company?.companyType === CompanyType.BP && this.userProfileInfoData?.userGroup?.code !== PARTNER_LAM_USER;
  }

  public isPartnerUser(): boolean {
    return this.userProfileInfoData?.company?.companyType === CompanyType.BP;
  }

  // used to display the permissions view for others than ACSL users & Customer users
  public isWebshopPermissionsView():boolean {
    return _.contains(webshopPermissionsUserTypes, this.getUserInfo()?.userType);
  }

  public createUserProfileRequest(approved:boolean, rejected:boolean):IUserProfileRequest {
    let location = util.copyAndExtend(this.userProfileInfoData.officeLocation);
    if (!location && this.userProfileInfoData.company) { //create a location with only the company
      location = <ILocationExtended>{
        companyCountry: {
          company: this.userProfileInfoData.company
        }
      };
    }

    return {
      countryId: this.userProfileInfoData.officeCountry?.id,
      firstname: this.userProfileInfoData.firstName,
      lastname: this.userProfileInfoData.lastName,
      email: this.userProfileInfoData.email,
      language: this.userProfileInfoData.language,
      gaLayout: this.userProfileInfoData.gaLayout,
      login: this.userProfileInfoData.username,
      title: this.userProfileInfoData.jobTitle,
      companyDepartment: this.userProfileInfoData.companyDepartment,
      directPhone: this.userProfileInfoData.directPhone,
      officePhone: this.userProfileInfoData.officePhone,
      cellPhone: this.userProfileInfoData.cellPhone,
      faxNumber: this.userProfileInfoData.fax,
      location,
      subCompany: this.userProfileInfoData.subCompany,
      office: this.userProfileInfoData.office,
      password: this.userProfileInfoData.password,
      userRegistrationApproved: approved,
      userRegistrationRejected: rejected,
      preferredCommunication: this.userProfileInfoData.preferredCommunication,
      privacyAgreed: this.userProfileInfoData.privacyAgreed,
      comment: this.userProfileInfoData.comment,
      genericUser: this.userProfileInfoData.genericUser,
      localCredentialsAlwaysAllowed: this.userProfileInfoData.localCredentialsAlwaysAllowed,
      signupCompanyType: (this.userProfileInfoData.companyType && this.userProfileInfoData.companyType.id) || this.userProfileInfoData.signupCompanyType,
      signupCountry: this.userProfileInfoData.officeCountry,
      userTraining: {
        lastTrainingDate: this.userProfileInfoData.lastTrainingDate,
        trainingStatus: <string>(this.userProfileInfoData.trainingStatus && this.userProfileInfoData.trainingStatus.id),
        acslComment: this.userProfileInfoData.trainingComments,
        responsive: this.userProfileInfoData.responsive,
        usingGA: this.userProfileInfoData.usingGA,
        canDemoGA: this.userProfileInfoData.canDemoGA
      },
      weChatId: this.userProfileInfoData.weChatId,
      whatsappId: this.userProfileInfoData.whatsAppId,
      mailSubscription: this.notifications,
      linkedin: this.userProfileInfoData.linkedin,
      fluentEnglish: <boolean>this.userProfileInfoData.fluentEnglish?.id,
      updateMethod: this.userProfileInfoData.updateMethod,
      updateComments: this.userProfileInfoData.updateComments,
      updateFrequency: this.userProfileInfoData.updateFrequency,
      timeSlotForGAUpdates: this.userProfileInfoData.timeSlotForGAUpdates,
      checkpoint: this.userProfileInfoData.checkpoint
    };
  }

  private permissionsChanged():boolean {
    const newGenericUserId:number = this.userProfileInfoData.genericUser && this.userProfileInfoData.genericUser.id;
    const oldGenericUserId:number = this.backupServerData && this.backupServerData.userProfile && this.backupServerData.userProfile.genericUser && this.backupServerData.userProfile.genericUser.id;

    if (newGenericUserId !== oldGenericUserId) {
      return true;
    }

    if (!this.isWebshopPermissionsView()) {
      return false;
    }

    const newAllWebshops:boolean = this.webshopPermissions.permissionToAllWebshops && this.webshopPermissions.permissionToAllWebshops.id;
    const oldAllWebshops:boolean = this.backupServerData.permissions && (<IWebshopPermissionResponse>this.backupServerData.permissions).permissionToAllWebshops;

    const newSelectedWebshops:IDualMultiSelectElement[] = this.webshopPermissions.selectedWebshops || [];
    const oldSelectedWebshops:IDualMultiSelectElement[] = this.backupServerData.permissions && ((<IWebshopPermissionResponse>this.backupServerData.permissions).selectedWebshops || []);

    return newAllWebshops !== oldAllWebshops || !_.isEqual(newSelectedWebshops, oldSelectedWebshops);
  }

  private createPermissionRequest(genericUserId:number):IPermissionRequest {
    const permissionRequest:IPermissionRequest = {
      userId: this.getUserProfileId(),
      genericUserId: genericUserId
    };

    if (this.isWebshopPermissionsView() && !_.isEqual(this.backupServerData.permissions, this.permissions)) {
      permissionRequest.allWebshops = this.webshopPermissions.permissionToAllWebshops && this.webshopPermissions.permissionToAllWebshops.id;
      if (this.webshopPermissions.selectedWebshops && this.webshopPermissions.selectedWebshops.length) {
        permissionRequest.selectedWebshops = this.webshopPermissions.selectedWebshops;
      }
    }

    return permissionRequest;
  }

  public saveAndLoadPermissions = ():Promise<IWebshopPermissionResponse | IOrderQuotePermissionResponse> => {
    const genericUserId = this.userProfileInfoData.genericUser && this.userProfileInfoData.genericUser.id;
    if (genericUserId && this.permissionsChanged()) {
      const permissionRequest:IPermissionRequest = this.createPermissionRequest(genericUserId);
      return this.userProfileDataService.setPermissions(this.getUserProfileId(), permissionRequest)
        .then(() => this.loadPermissions(true));
    } else {
      return this.loadPermissions(true);
    }
  };

  public notificationsChanged(userNotifications:IUserNotifications):void {
    this.notifications = userNotifications;
  }

  public setGenericUserFn(genericUserSelectionFn:Function):void {
    this._genericUserFn = genericUserSelectionFn;
  }

  public clear():void {
    this.userProfilePromise = undefined;
    this.permissions = undefined;
    this.userProfileId = undefined;
    this.userProfileInfoData = undefined;
    this.companyId = undefined;
    this.countryId = undefined;
    this.webshopId = undefined;
    this.webshopPermissions = undefined;
    this.genericUsers = undefined;
    this.notifications = undefined;
    this.backupServerData = {
      userProfile: undefined,
      permissions: undefined
    };
  }
}